// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {i18n, i18nForLocale} from "../i18n/I18n";
import {fetchJSON} from "../utils/fetch";
import {https} from "../utils/http";
import * as array from "../utils/array";
import {LocationService} from "../services/LocationService";
import {ConfigService} from "../services/ConfigService";
import {AnalyticsService} from "../services/AnalyticsService";

// Private Definitions ------------------------------------------------------->

interface CategoryDictionaryJSON {
	search_terms: ItemCategory[];
	cat_order: string[];
}

function _clone(obj: any) {
	const cloned = {
		name: obj.name,
		cat: obj.cat,
		canonical_category: obj.canonical_category,
		icon_url: obj.icon_url,
		icon_asset_name: obj.icon_asset_name,
		is_clean: obj.is_clean
	};

	return cloned;
}

// NOTE: "Other", "My Clippings", "Photo Lists" are currently NOT returned by Backflipp (inside the `cat_order` array).
const CATEGORY_ORDER_EXTRAS = {
	"en": [i18nForLocale("category_other", "en"), i18nForLocale("category_my_clippings", "en"), i18nForLocale("category_photo_lists", "en")],
	"fr": [i18nForLocale("category_other", "fr"), i18nForLocale("category_my_clippings", "fr"), i18nForLocale("category_photo_lists", "fr")]
};

// Public Definitions -------------------------------------------------------->

/* Defines item categorization */
export interface ItemCategory {
	name: string;
	cat: string;
	canonical_category: string; // NOTE: this value is locale INDEPENDENT (e.g. "dairy_eggs" even if locale is FRENCH!)
	icon_url: string;
	icon_asset_name: string;
	is_clean: boolean;
}

@service
export class CategoryService extends Service {

	@inject location: LocationService;
	@inject config: ConfigService;
	@inject analytics: AnalyticsService;

	private _categoryCache: {[key: string]: ItemCategory} = {};
	private _categoryOrderCache: {[key: string]: string[]} = {};

	getCategoryForItems(items: string[]): Promise<ItemCategory[]> {
		const locale = this.location.acceptsLocale("en") ? "en" : "fr";
		const result: ItemCategory[] = [];
		const uncatItems: string[] = [];
		items.forEach((item) => {
			if (!this.hasCategory(item)) {
				uncatItems.push(item);
			} else {
				// We need to preserve the original item name.
				let catObj = _clone(this.getCategoryObj(item));
				catObj.name = item;
				result.push(catObj);
			}
		});

		if (uncatItems.length === 0) {
			return Promise.resolve(result);
		}

		return fetchJSON<CategoryDictionaryJSON>(
			`${this.config.GATEFLIPP_SERVER}/cat_dict?locale=${locale}&all_attributes=true&terms=${encodeURIComponent(uncatItems.join(","))}&sid=${this.analytics.sessionId}`)
			.then((data: CategoryDictionaryJSON) => {
				if (!this.hasCategoryOrder(locale) && Array.isArray(data.cat_order)) {
					this._categoryOrderCache[locale] = data.cat_order.concat(CATEGORY_ORDER_EXTRAS[locale]);
				}

				const uncatResults: ItemCategory[] = [];
				uncatItems.forEach((term) => {

					// Backflipp preserves the same name as passed to the query e.g. "BaBY WIPES" will be returned as "BaBY WIPES".
					// Thus, there is no need to have lower-case comparison between the search term and each item name.
					const result = array.find(data.search_terms, (entry) => entry.name === term);
					let catObj = {
						name: term,
						cat: i18n("category_other"),
						canonical_category: "other",
						icon_url: "",
						icon_asset_name: "",
						is_clean: false
					};
					if (result) {
						catObj = {
							name: result.name,
							cat: result.cat,
							canonical_category: result.canonical_category,
							icon_url: result.icon_url,
							icon_asset_name: result.icon_asset_name,
							is_clean: result.is_clean
						};
					}

					this._categoryCache[term.toLowerCase()] = catObj;
					uncatResults.push(catObj);
				});

				return result.concat(uncatResults);
		});
	}

	hasCategory(item: string) {
		return this._categoryCache[item.toLowerCase()];
	}

	getCategoryObj(item: string) {
		return this._categoryCache[item.toLowerCase()];
	}

	hasCategoryOrder(locale: string) {
		return this._categoryOrderCache[locale];
	}

	getCategoryOrder(locale: string): string[] {
		return this._categoryOrderCache[locale] || [];
	}

	getCanonicalCategoryName(item: string) {
		if (this._categoryCache[item.toLowerCase()]) {
			return this._categoryCache[item.toLowerCase()].canonical_category;
		} else {
			return "other";
		}
	}

	iconAssetNameExists(iconAssetName: string, keysToCheck: string[]) {
		const catObj = this._categoryCache;
		let found = false;

		for (const key of keysToCheck) {
			if (catObj[key] && catObj[key].icon_asset_name === iconAssetName) {
				found = true;
			}
		}

		return found;
	}

	getItemImageURL(item: string) {
		const catObj = this._categoryCache[item.toLowerCase()];

		// Worst-case scenario, we'll use the 'other default' image.
		let url = "https://f.wishabi.net/creative/Distribution/images/sl/category/other_default.svg";

		if (catObj) {
			if (catObj.icon_url) {
				url = https(catObj.icon_url);
			} else {
				const canonicalCat = catObj.canonical_category;
				// Image is not available, so we need to return a default icon FOR THIS category.
				url = `https://f.wishabi.net/creative/Distribution/images/sl/category/${canonicalCat}_default.svg`;
			}
		}

		return url;
	}

	getCategoySlugToNameMapping(): { [uri: string]: string} {
		return {
			"all-flyers": "All Flyers",
			"groceries": "Groceries",
			"home-and-garden": "Home & Garden",
			"pharmacy": "Pharmacy",
			"electronics": "Electronics",
			"baby-and-kids": "Baby & Kids",
			"fashion": "Fashion",
			"automotive": "Automotive",
			"sporting-goods": "Sporting Goods",
			"pets": "Pets",
			"office": "Office",
			"black-friday": "Black Friday",
			"back-to-school": "Back to School",
			"boxing-day": "Boxing Day",
			"general-merchandise": "General Merchandise",
			"specialty": "Specialty",
			"online-only": "Online Only",
			"halloween": "Halloween",
			"thanksgiving": "Thanksgiving",
			"cyber-monday": "Cyber Monday",
			"holidays": "Holidays",
			"holiday-gifting": "Holiday Gifting",
			"christmas": "Christmas",
			"new-years-eve": "New Year's Eve",
			"new-years": "New Years",
			"valentines-day": "Valentine's Day",
			"presidents-day": "President's Day",
			"easter": "Easter",
			"mothers-day": "Mother's Day",
			"fathers-day": "Father's Day",

			"tout": "All Flyers",
			"epicerie": "Groceries",
			"maison-et-jardin": "Home & Garden",
			"pharmacie": "Pharmacy",
			"electronique": "Electronics",
			"enfants-et-bebes": "Baby & Kids",
			"mode": "Fashion",
			"automobile": "Automotive",
			"sports": "Sporting Goods",
			"animaux": "Pets",
			"bureau": "Office",
			"vendredi-fou": "Black Friday",
			"le-lendemain-de-noel": "Boxing Day",
			"la-rentree-scolaire": "Back to School",
			"marchandises-generales": "General Merchandise",
			"specialite": "Specialty",
			"uniquement-en-ligne": "Online Only",
			// Duplicate identifier - Halloween is the same in EN and FR
			// "halloween": "Halloween",
			"action-de-grace": "Thanksgiving",
			"cyberlundi": "Cyber Monday",
			"temps-des-fetes": "Holiday Gifting",
			"noel": "Christmas",
			"veille-du-jour-de-lan": "New Year's Eve",
			"jour-de-lan": "New Years",
			"saintvalentin": "Valentine's Day",
			"jour-des-presidents": "President's Day",
			"paques": "Easter",
			"fetes-des-meres": "Mother's Day",
			"fetes-des-peres": "Father's Day",
			"restaurants": "Restaurants"
		};
	}

	getCouponsToSlugMapping(): { [uri: string]: string} {
		return {
			"all-deals": "All Deals",
			"baby": "Baby",
			"grocery": "Grocery",
			"health-and-beauty": "Health & Beauty",
			"household": "Household",
			"pets": "Pets",
			"other": "Other",

			"tout": "All Deals",
			"bebe": "Baby",
			"epicerie": "Grocery",
			"sante-et-beaute": "Health & Beauty",
			"maison": "Household",
			"animaux": "Pets",
			"autre": "Other"
		};
	}
}
